import React from "react";
import type { LensSystem as LensSystemType, Lens, LensRead } from "@/lib/unifiedScoring";

/**
 * PQ-5c / PQ-5d — Lens system.
 *
 * Renders up to four evidence rows under the verdict:
 *   Fit · Suits you · Pairs with · Fills a gap
 *
 * Brief 4 principle: nothing below the verdict may contradict it. Each
 * lens carries an explicit +/− glyph so the user can triangulate the
 * verdict against its evidence (and override decisions are visible —
 * e.g. Skip + Pairs with ✓✓ surfaces "yes it pairs even though sizing
 * looks risky").
 *
 * PQ-5d additions:
 *  - `pairs_with` / `fills_gap` lenses (renamed from `wardrobe` / `youll_wear`)
 *  - Guest signup nudge when the wardrobe lenses can't be computed
 *  - Sparse-wardrobe nudge when authed users have <5 wardrobe items
 */

interface LensSystemProps {
  lenses: LensSystemType | null | undefined;
  /** Guest mode — wardrobe lenses suppressed; show signup nudge instead. */
  isGuest?: boolean;
  /**
   * Active+eligible wardrobe item count (authed users). When defined and
   * 1..4, surfaces the sparse-wardrobe nudge if `pairs_with` didn't fire.
   */
  wardrobeItemCount?: number | null;
  /** Tap handler for the guest signup nudge (analytics + navigation). */
  onSignupTapped?: () => void;
  /** Tap handler for the sparse-wardrobe nudge. */
  onAddWardrobeTapped?: () => void;
}

const READ_GLYPH: Record<Exclude<LensRead, null>, string> = {
  "++": "✓",
  "+": "✓",
  neutral: "—",
  "-": "✗",
  "--": "✗✗",
};

const READ_COLOR: Record<Exclude<LensRead, null>, string> = {
  "++": "#3B6D11",
  "+": "#3B6D11",
  neutral: "#6B6560",
  "-": "#993C1D",
  "--": "#993C1D",
};

type LensKey = "fit" | "suits_you" | "pairs_with" | "fills_gap";

const LENS_LABELS: Record<LensKey, string> = {
  fit: "Fit",
  suits_you: "Suits you",
  pairs_with: "Pairs with",
  fills_gap: "Fills a gap",
};

// Suits-you reuses the editorial purple from the PQ-5a-fix styling note so the
// styling content's visual identity carries into the lens system. All other
// lenses use the warm neutral ink so Suits-you stays the signature voice.
const LENS_COPY_COLOR: Record<LensKey, string> = {
  fit: "#4A4540",
  suits_you: "#534AB7",
  pairs_with: "#4A4540",
  fills_gap: "#4A4540",
};

function LensRow({
  label,
  lens,
  copyColor,
  isLast,
}: {
  label: string;
  lens: Lens;
  copyColor: string;
  isLast: boolean;
}) {
  if (lens.read == null) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 0",
        borderBottom: isLast ? "none" : "0.5px solid #E8E0D5",
      }}
    >
      <span
        style={{
          width: 78,
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 500,
          color: "#2C2820",
          lineHeight: 1.4,
        }}
      >
        {label}
      </span>
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 600,
          color: READ_COLOR[lens.read],
          lineHeight: 1.4,
          minWidth: 18,
        }}
      >
        {READ_GLYPH[lens.read]}
      </span>
      {lens.copy && (
        <span
          style={{
            fontFamily: "var(--font-display, var(--font-serif, serif))",
            fontStyle: "italic",
            fontSize: 14,
            fontWeight: 400,
            color: copyColor,
            lineHeight: 1.4,
            flex: 1,
          }}
        >
          {lens.copy}
        </span>
      )}
    </div>
  );
}

function GuestSignupNudge({ onTap }: { onTap?: () => void }) {
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        width: "100%",
        marginTop: 10,
        padding: "12px 14px",
        background: "rgba(232, 90, 79, 0.06)",
        border: "0.5px solid rgba(232, 90, 79, 0.18)",
        borderRadius: 12,
        textAlign: "left",
        cursor: onTap ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display, var(--font-serif, serif))",
          fontStyle: "italic",
          fontSize: 14,
          color: "#4A4540",
          lineHeight: 1.4,
        }}
      >
        Want to know if this pairs with what you own?
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--l2f-coral)",
          letterSpacing: "0.02em",
        }}
      >
        Sign up · add wardrobe →
      </span>
    </button>
  );
}

function SparseWardrobeNudge({
  wardrobeItemCount,
  onTap,
}: {
  wardrobeItemCount: number;
  onTap?: () => void;
}) {
  const remaining = Math.max(1, 5 - wardrobeItemCount);
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        width: "100%",
        marginTop: 10,
        padding: "12px 14px",
        background: "rgba(74, 69, 64, 0.04)",
        border: "0.5px solid rgba(74, 69, 64, 0.12)",
        borderRadius: 12,
        textAlign: "left",
        cursor: onTap ? "pointer" : "default",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display, var(--font-serif, serif))",
          fontStyle: "italic",
          fontSize: 14,
          color: "#4A4540",
          lineHeight: 1.4,
        }}
      >
        Add {remaining} more item{remaining === 1 ? "" : "s"} to your wardrobe to see Pairs with.
      </span>
    </button>
  );
}

export function LensSystem({
  lenses,
  isGuest = false,
  wardrobeItemCount = null,
  onSignupTapped,
  onAddWardrobeTapped,
}: LensSystemProps) {
  if (!lenses) return null;

  const populated: Array<{ key: LensKey; lens: Lens }> = [];
  if (lenses.fit) populated.push({ key: "fit", lens: lenses.fit });
  if (lenses.suits_you) populated.push({ key: "suits_you", lens: lenses.suits_you });
  if (lenses.pairs_with) populated.push({ key: "pairs_with", lens: lenses.pairs_with });
  if (lenses.fills_gap) populated.push({ key: "fills_gap", lens: lenses.fills_gap });

  // Guest nudge — guest users can't compute wardrobe-derived lenses.
  const showGuestNudge =
    isGuest && !lenses.pairs_with && !lenses.fills_gap;

  // Sparse-wardrobe nudge — authed users with 1..4 items and no Pairs with.
  const showSparseNudge =
    !isGuest &&
    wardrobeItemCount != null &&
    wardrobeItemCount > 0 &&
    wardrobeItemCount < 5 &&
    !lenses.pairs_with;

  if (populated.length === 0 && !showGuestNudge && !showSparseNudge) {
    return null;
  }

  return (
    <div aria-label="The read evidence" style={{ marginBottom: 12 }}>
      {populated.map(({ key, lens }, idx) => (
        <LensRow
          key={key}
          label={LENS_LABELS[key]}
          lens={lens}
          copyColor={LENS_COPY_COLOR[key]}
          isLast={idx === populated.length - 1}
        />
      ))}
      {showGuestNudge && <GuestSignupNudge onTap={onSignupTapped} />}
      {showSparseNudge && wardrobeItemCount != null && (
        <SparseWardrobeNudge
          wardrobeItemCount={wardrobeItemCount}
          onTap={onAddWardrobeTapped}
        />
      )}
    </div>
  );
}

export default LensSystem;
